/**
 * Turning a failure into something to show a person.
 *
 * The mapping is by the API's problem *type* where there is one, because the type is the stable part
 * of the contract: `/api/v1` promises the slug does not move, while a status could be served
 * differently later. When there is no problem body — a network failure, a response that did not match
 * the contract — the error's own `kind` identifies it.
 *
 * The API already sends a Vietnamese `detail` for most failures, and this deliberately does not use
 * it. A server's detail explains what the server saw ("Phiên đã hết hạn"); what the user needs is what
 * to do next ("đăng nhập lại"). Those are different sentences and the second belongs to the client,
 * where it can be phrased for the screen the user is actually on. `detail` is still carried on the
 * error for a log or a support ticket.
 */

import { ApiError } from "@trippool/api-client";

import { copy } from "../copy.ts";

/** Every key `copy.problem` knows. */
export type ProblemKey = keyof typeof copy.problem;

export function isProblemKey(value: string): value is ProblemKey {
  return Object.hasOwn(copy.problem, value);
}

/** The message to show for any thrown value. */
export function messageFor(error: unknown): string {
  if (!(error instanceof ApiError)) {
    // A bug in this app rather than a failed request. Reported honestly instead of being blamed on
    // the network, which would send the user to check their connection for nothing.
    return copy.problem.unknown;
  }

  // `kind` first: a network failure and a contract mismatch have no problem type, and both are more
  // specific than "something went wrong".
  if (error.kind === "network") return copy.problem.network;
  if (error.kind === "invalid-response") return copy.problem["invalid-response"];

  const slug = error.problemType;
  if (slug !== null && isProblemKey(slug)) return copy.problem[slug];
  return copy.problem.unknown;
}

/**
 * The reason the server gave for refusing, when showing it is the point.
 *
 * `messageFor` is right for the failures the *client* knows how to describe, and it deliberately ignores
 * the server's `detail`. This is the opposite case: a refusal whose condition only the server can see —
 * whether a member has a transaction, so whether they may be deleted. The client cannot re-derive that
 * from anything it holds, so the server's own sentence is the truth to show, and this hands it over
 * unchanged. It falls back to `messageFor` when the response carried no detail at all, so a network
 * failure still reads as a network failure rather than as an empty line.
 */
export function serverReasonFor(error: unknown): string {
  if (error instanceof ApiError && error.detail !== undefined && error.detail !== "") return error.detail;
  return messageFor(error);
}

/**
 * Field-level messages from a 400, for a form to render beside its inputs.
 *
 * The API prefixes each `field` with where it came from (`body.`, `query.`, `params.`). Stripping the
 * prefix is right here because a form only ever submits a body, and showing "body.email" beside an
 * input labelled "Email" reads like a bug.
 */
export function fieldMessagesFor(error: unknown): Map<string, string[]> {
  const messages = new Map<string, string[]>();
  if (!(error instanceof ApiError)) return messages;
  for (const { field, message } of error.fieldErrors) {
    const name = field.replace(/^(body|query|params)\./, "");
    const existing = messages.get(name) ?? [];
    existing.push(message);
    messages.set(name, existing);
  }
  return messages;
}
